/* ============================================================================
 * КАНОНИЧЕСКИЕ СТАТУСЫ ЗАЯВОК (задача №7)
 *
 * БЫЛО: `status?: string` — произвольная строка. Любая опечатка или новый бэкенд
 * молча ломала отображение, а счётчики в панели считали «как попало».
 *
 * СТАЛО: закрытый набор + функция разбора с ЯВНЫМ fallback'ом, который виден
 * в UI (нет молчаливого превращения неизвестного статуса в «готово»).
 *
 * Набор взят из соглашения о статусах заявки СППИ:
 *   pending       — заявка создана, ждёт рассмотрения диспетчером;
 *   ready         — согласована и готова к вылету (можно лететь);
 *   rejected      — отклонена (отказ, превышение ограничений);
 *   completed     — полёт завершён (архив);
 *   under_review  — в работе у диспетчера;
 *   followup      — требуется уточнение у пилота/оператора;
 *   critical      — нарушение ограничений, требует немедленного реагирования.
 * ========================================================================= */

export type DeclarationStatus =
  | 'pending'
  | 'ready'
  | 'rejected'
  | 'completed'
  | 'under_review'
  | 'followup'
  | 'critical'

/** Канонический порядок статусов — от рабочего к архивному. */
export const DECLARATION_STATUSES: readonly DeclarationStatus[] = [
  'pending',
  'under_review',
  'ready',
  'followup',
  'critical',
  'rejected',
  'completed',
] as const

/** Русские подписи панелей. */
export const DECLARATION_STATUS_LABEL: Record<DeclarationStatus, string> = {
  pending: 'Ожидает',
  under_review: 'На рассмотрении',
  ready: 'Согласована',
  followup: 'Требует уточнения',
  critical: 'Критично',
  rejected: 'Отклонена',
  completed: 'Завершена',
}

/** Тон интерфейса для каждого статуса (палитра из theme.css). */
export const DECLARATION_STATUS_TONE: Record<
  DeclarationStatus,
  'approved' | 'warning' | 'critical-error' | 'pending' | 'active' | 'selected'
> = {
  pending: 'pending',
  under_review: 'active',
  ready: 'approved',
  followup: 'warning',
  critical: 'critical-error',
  rejected: 'critical-error',
  completed: 'pending',
}

/** Статусы, требующие активного внимания оператора (для бейджей-подсветки). */
export const STATUS_ATTENTION: readonly DeclarationStatus[] = [
  'critical',
  'followup',
  'rejected',
] as const

/**
 * Разбор статуса БЕЗ молчаливых искажений.
 *
 * Бэкенд Flight Blender отдаёт свободную строку (`state`/`is_approved`), поэтому
 * приводим её к канону ЗДЕСЬ и возвращаем флаг `raw`, если исходное значение не
 * совпало ни с одним известным: UI покажет его дословно, а не спрячет.
 */
export function parseDeclarationStatus(
  raw: unknown,
  opts: { isApproved?: boolean } = {},
): { status: DeclarationStatus; raw?: string } {
  const s = String(raw ?? '').trim().toLowerCase()

  // Явные совпадения с каноном (в т.ч. числовые коды ДТОПС, встречающиеся в FB).
  const table: Record<string, DeclarationStatus> = {
    '1': 'pending',
    '2': 'ready',
    '3': 'rejected',
    '4': 'completed',
    pending: 'pending',
    ready: 'ready',
    approved: 'ready',
    accepted: 'ready',
    rejected: 'rejected',
    declined: 'rejected',
    denied: 'rejected',
    completed: 'completed',
    closed: 'completed',
    archived: 'completed',
    under_review: 'under_review',
    'in_review': 'under_review',
    review: 'under_review',
    followup: 'followup',
    follow_up: 'followup',
    'need_info': 'followup',
    clarification: 'followup',
    critical: 'critical',
    violation: 'critical',
    conflict: 'critical',
  }
  if (table[s]) return { status: table[s] }

  // Строка не распознана: если есть is_approved — выводим из него, ИНАЧЕ 'pending'
  // (не 'ready' — иначе несогласованная заявка выглядела бы как согласованная).
  if (opts.isApproved === true) return { status: 'ready' }
  if (opts.isApproved === false) return { status: 'pending' }
  return { status: 'pending', raw: s || String(raw ?? '') }
}

/** Счётчики по статусам — источник данных для бейджей-счётчиков. */
export function countByStatus(
  items: Array<{ status: DeclarationStatus }>,
): Record<DeclarationStatus, number> {
  const out = Object.fromEntries(
    DECLARATION_STATUSES.map((s) => [s, 0]),
  ) as Record<DeclarationStatus, number>
  for (const it of items) out[it.status] = (out[it.status] ?? 0) + 1
  return out
}
